/**
 * App Routes
 * Main routing configuration with lazy loading
 * Reference: ITL 03-Frontend-Architecture-Components.md section 5
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import RoleBasedRoute from './RoleBasedRoute';
import { ROUTES } from './routeConfig';

// Loading Component
const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="spinner w-16 h-16"></div>
  </div>
);

// Lazy load pages
// Public Pages
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));
const FreeGamesPage = lazy(() => import('../pages/public/FreeGamesPage'));

// Student Pages
const StudentDashboard = lazy(() => import('../pages/student/StudentDashboardPage'));
const StudentCourses = lazy(() => import('../pages/student/Courses'));
const StudentCourseDetail = lazy(() => import('../pages/student/CourseDetail'));
const CodingExercise = lazy(() => import('../pages/student/CodingExercise'));
const StudentChallenges = lazy(() => import('../pages/student/Challenges'));
const StudentChallengeDetail = lazy(() => import('../pages/student/ChallengeDetail'));
const StudentSubmissions = lazy(() => import('../pages/student/Submissions'));
const StudentProgress = lazy(() => import('../pages/student/Progress'));
const StudentAchievements = lazy(() => import('../pages/student/Achievements'));
const StudentProfile = lazy(() => import('../pages/student/Profile'));
const StudentSettings = lazy(() => import('../pages/student/Settings'));

// Game Pages (CodeCombat-style)
const GameWorld = lazy(() => import('../pages/student/GameWorld'));
const GameLessonViewer = lazy(() => import('../components/game/GameLessonViewer/GameLessonViewer'));

// Teacher Pages
const TeacherDashboard = lazy(() => import('../pages/teacher/TeacherDashboardPage'));
const TeacherClasses = lazy(() => import('../pages/teacher/Classes'));
const TeacherCourses = lazy(() => import('../pages/teacher/Courses'));
const TeacherGrading = lazy(() => import('../pages/teacher/Grading'));
const TeacherAnalytics = lazy(() => import('../pages/teacher/Analytics'));
const TeacherStudents = lazy(() => import('../pages/teacher/Students'));
const TeacherProfile = lazy(() => import('../pages/teacher/Profile'));
const TeacherSettings = lazy(() => import('../pages/teacher/Settings'));

// Admin Pages
const AdminDashboard = lazy(() => import('../pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('../pages/admin/Users'));
const AdminCourses = lazy(() => import('../pages/admin/Courses'));
const AdminSystem = lazy(() => import('../pages/admin/System'));
const AdminAnalytics = lazy(() => import('../pages/admin/Analytics'));

// Error Pages
const NotFound = lazy(() => import('../pages/errors/NotFound'));
const Unauthorized = lazy(() => import('../pages/errors/Unauthorized'));

const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public Routes */}
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
        <Route path={ROUTES.FREE_GAMES} element={<FreeGamesPage />} />

        {/* Student Routes */}
        <Route
          path={ROUTES.STUDENT.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentDashboard />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.COURSES}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentCourses />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.COURSE_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentCourseDetail />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.LESSON_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <CodingExercise />
            </RoleBasedRoute>
          }
        />
        {/* Game Routes (CodeCombat-style) */}
        <Route
          path={ROUTES.STUDENT.GAME_WORLD}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <GameWorld />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.GAME_LESSON}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <GameLessonViewer />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.CHALLENGES}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentChallenges />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.CHALLENGE_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentChallengeDetail />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.SUBMISSIONS}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentSubmissions />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PROGRESS}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentProgress />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.ACHIEVEMENTS}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentAchievements />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PROFILE}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentProfile />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['student']}>
              <StudentSettings />
            </RoleBasedRoute>
          }
        />

        {/* Teacher Routes */}
        <Route
          path={ROUTES.TEACHER.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherDashboard />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.CLASSES}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherClasses />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.COURSES}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherCourses />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.GRADING}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherGrading />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.ANALYTICS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherAnalytics />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.STUDENTS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherStudents />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.PROFILE}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherProfile />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherSettings />
            </RoleBasedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path={ROUTES.ADMIN.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.USERS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminUsers />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.COURSES}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminCourses />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SYSTEM}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSystem />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.ANALYTICS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminAnalytics />
            </RoleBasedRoute>
          }
        />

        {/* Default Redirects */}
        <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.LOGIN} replace />} />
        <Route path={ROUTES.STUDENT.ROOT} element={<Navigate to={ROUTES.STUDENT.DASHBOARD} replace />} />
        <Route path={ROUTES.TEACHER.ROOT} element={<Navigate to={ROUTES.TEACHER.DASHBOARD} replace />} />
        <Route path={ROUTES.ADMIN.ROOT} element={<Navigate to={ROUTES.ADMIN.DASHBOARD} replace />} />

        {/* Error Pages */}
        <Route path={ROUTES.UNAUTHORIZED} element={<Unauthorized />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;

